import React, { useRef } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, MenuItem, TextField, Typography,
} from '@mui/material';
import {
  UploadFile as UploadIcon, Add as AddIcon, Close as RemoveIcon,
} from '@mui/icons-material';
import { expiryStatus } from './tradeLicenceForm';

const FS = '0.8125rem';
const fieldProps = {
  size: 'small', fullWidth: true,
  InputProps: { sx: { fontSize: FS } }, InputLabelProps: { sx: { fontSize: FS } },
};
const dateProps = { ...fieldProps, type: 'date', InputLabelProps: { shrink: true, sx: { fontSize: FS } } };
const EMIRATES = ['Abu Dhabi', 'Ajman', 'Dubai', 'Fujairah', 'Ras Al Khaimah', 'Sharjah', 'Umm Al Quwain'];
const subLabelSx = { fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mt: 1.5, mb: 0.5 };

// Upload box shown at the top of the client dialog.
export const TradeLicenceImport = ({ importing, error, warnings, imported, onImport, onDismissError }) => {
  const inputRef = useRef(null);
  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 1,
          border: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover',
        }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={importing ? <CircularProgress size={14} color="inherit" /> : <UploadIcon fontSize="small" />}
          disabled={importing}
          onClick={() => inputRef.current?.click()}
          sx={{ textTransform: 'none', fontSize: FS, flexShrink: 0 }}
        >
          {importing ? 'Reading licence…' : imported ? 'Import again' : 'Import trade licence'}
        </Button>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {imported
            ? 'Imported — check the values below before saving.'
            : 'PDF or photo of the licence. AI fills the form; you check it before saving.'}
        </Typography>
        <input
          ref={inputRef}
          type="file"
          hidden
          accept="application/pdf,image/jpeg,image/png,image/webp"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) onImport(file);
          }}
        />
      </Box>
      {error && <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }} onClose={onDismissError}>{error}</Alert>}
      {warnings?.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1, fontSize: '0.75rem' }}>{warnings.join(' · ')}</Alert>
      )}
    </Box>
  );
};

const RowsEditor = ({ label, rows, columns, onChange, addLabel }) => {
  const update = (i, key, value) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  const blank = Object.fromEntries(columns.map((c) => [c.key, '']));
  return (
    <Box>
      <Typography sx={subLabelSx}>{label}</Typography>
      {rows.map((r, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
          {columns.map((c) => (
            <TextField
              key={c.key}
              label={c.label}
              value={r[c.key]}
              onChange={(e) => update(i, c.key, e.target.value)}
              type={c.type || 'text'}
              {...fieldProps}
              sx={{ flex: c.flex || 1, minWidth: 0 }}
            />
          ))}
          <IconButton size="small" aria-label={`Remove ${label.toLowerCase()} row`}
            onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>
            <RemoveIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={() => onChange([...rows, blank])}
        sx={{ textTransform: 'none', fontSize: '0.75rem', px: 0.5 }}>
        {addLabel}
      </Button>
    </Box>
  );
};

// The editable Trade licence details. Collapsed until there is something to show or the user opens it.
const TradeLicenceSection = ({ licence, onChange, open, onOpen, today }) => {
  const set = (key) => (e) => onChange({ ...licence, [key]: e.target.value });
  const status = expiryStatus(licence.expiryDate, today);

  return (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: FS, fontWeight: 600, flexGrow: 1 }}>Trade licence</Typography>
        {status && <Chip size="small" label={status.label} color={status.color} sx={{ fontSize: '0.6875rem', height: 22 }} />}
        {!open && (
          <Button size="small" onClick={onOpen} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Add manually</Button>
        )}
      </Box>
      {!open ? (
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          No licence details. Import the licence above or add them manually.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1 }}>
            <TextField label="Licence number" value={licence.licenceNumber} onChange={set('licenceNumber')} {...fieldProps} />
            <TextField label="Trade name" value={licence.tradeName} onChange={set('tradeName')} {...fieldProps} />
            <TextField label="Legal form" value={licence.legalForm} onChange={set('legalForm')} placeholder="e.g. LLC, Sole Establishment" {...fieldProps} />
            <TextField label="Issuing authority" value={licence.issuingAuthority} onChange={set('issuingAuthority')} {...fieldProps} />
            <TextField select label="Emirate" value={licence.emirate} onChange={set('emirate')} {...fieldProps}>
              <MenuItem value=""><em>None</em></MenuItem>
              {EMIRATES.map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
            </TextField>
            <TextField label="Register number" value={licence.registerNumber} onChange={set('registerNumber')} {...fieldProps} />
            <TextField label="Chamber number" value={licence.chamberNumber} onChange={set('chamberNumber')} {...fieldProps} />
            <TextField label="Trade name (Arabic)" value={licence.tradeNameArabic} onChange={set('tradeNameArabic')}
              {...fieldProps} inputProps={{ dir: 'rtl' }} />
            <TextField label="Issue date" value={licence.issueDate} onChange={set('issueDate')} {...dateProps} />
            <TextField label="Expiry date" value={licence.expiryDate} onChange={set('expiryDate')} {...dateProps} />
          </Box>
          <RowsEditor
            label="Owners"
            addLabel="Add owner"
            rows={licence.owners}
            onChange={(owners) => onChange({ ...licence, owners })}
            columns={[
              { key: 'name', label: 'Name', flex: 3 },
              { key: 'nationality', label: 'Nationality', flex: 2 },
              { key: 'sharePercent', label: 'Share %', flex: 1, type: 'number' },
              { key: 'personNumber', label: 'Person no.', flex: 1 },
            ]}
          />
          <RowsEditor
            label="Managers"
            addLabel="Add manager"
            rows={licence.managers}
            onChange={(managers) => onChange({ ...licence, managers })}
            columns={[
              { key: 'name', label: 'Name', flex: 3 },
              { key: 'nationality', label: 'Nationality', flex: 2 },
              { key: 'personNumber', label: 'Person no.', flex: 1 },
            ]}
          />
          <RowsEditor
            label="Activities"
            addLabel="Add activity"
            rows={licence.activities}
            onChange={(activities) => onChange({ ...licence, activities })}
            columns={[
              { key: 'code', label: 'Code', flex: 1 },
              { key: 'name', label: 'Activity', flex: 4 },
            ]}
          />
        </>
      )}
    </Box>
  );
};

export default TradeLicenceSection;
